// SourceFileUpload.tsx - File upload with metadata (label, description, category, subcategory)

import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  TextField,
  IconButton,
  Collapse,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Upload,
  Close,
  Description,
  InsertDriveFile,
  PictureAsPdf,
  Image,
  CheckCircle,
} from '@mui/icons-material';

export interface FileWithMetadata {
  id: string;
  file: File;
  name: string;
  description: string;
  category: string;
  subCategory: string;
  size: number;
  type: string;
  uploadedAt: string;
}

interface SourceFileUploadProps {
  onFilesAdded: (files: FileWithMetadata[]) => void;
  existingFiles?: FileWithMetadata[];
  onRemove?: (id: string) => void;
  categories?: string[];
  subCategories?: Record<string, string[]>;
}

const CATEGORIES = [
  'Presentation', 'Document', 'PDF', 'Template', 'Reference',
  'Report', 'Proposal', 'Contract', 'Policy', 'Guide',
  'Research', 'Training', 'Brochure', 'Whitepaper', 'Case Study'
];

const SUBCATEGORIES: Record<string, string[]> = {
  'Presentation': ['Sales Deck', 'Pitch Deck', 'Board Presentation', 'Training Slides', 'Product Demo'],
  'Document': ['Word Doc', 'Text File', 'Markdown', 'Rich Text'],
  'PDF': ['Report PDF', 'Form PDF', 'Brochure PDF', 'Whitepaper PDF'],
  'Template': ['Email Template', 'Document Template', 'Slide Template', 'Spreadsheet Template'],
  'Reference': ['Brand Guidelines', 'Style Guide', 'Internal Reference', 'External Reference'],
  'Report': ['Annual Report', 'Quarterly Report', 'Performance Report', 'Analytics Report'],
  'Proposal': ['Sales Proposal', 'Project Proposal', 'Partnership Proposal', 'Investment Proposal'],
  'Contract': ['NDA', 'SLA', 'Service Agreement', 'Partnership Agreement'],
  'Policy': ['Company Policy', 'HR Policy', 'Security Policy', 'Compliance Policy'],
  'Guide': ['User Guide', 'Implementation Guide', 'Quick Start Guide', 'Best Practices'],
  'Research': ['Market Research', 'Competitor Analysis', 'Industry Research', 'User Research'],
  'Training': ['Training Material', 'Onboarding Guide', 'Workshop Content', 'Learning Module'],
  'Brochure': ['Product Brochure', 'Service Brochure', 'Company Brochure', 'Event Brochure'],
  'Whitepaper': ['Technical Whitepaper', 'Industry Whitepaper', 'Solution Whitepaper'],
  'Case Study': ['Client Case Study', 'Product Case Study', 'Implementation Case Study']
};

const SourceFileUpload: React.FC<SourceFileUploadProps> = ({
  onFilesAdded,
  existingFiles = [],
  onRemove,
  categories = CATEGORIES,
  subCategories = SUBCATEGORIES,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [formData, setFormData] = useState<{
    file: File | null;
    name: string;
    description: string;
    category: string;
    subCategory: string;
  }>({
    file: null,
    name: '',
    description: '',
    category: '',
    subCategory: '',
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setFormData({
        file,
        name: file.name.replace(/\.[^.]+$/, ''),
        description: '',
        category: '',
        subCategory: '',
      });
      setShowForm(true);
      setUploadError(null);
    }
    e.target.value = '';
  };

  const handleAddFile = () => {
    if (!formData.file) {
      setUploadError('Please select a file');
      return;
    }
    if (!formData.category) {
      setUploadError('Please select a category');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    const newFile: FileWithMetadata = {
      id: `file-${Date.now()}-${Math.random()}`,
      file: formData.file,
      name: formData.name || formData.file.name,
      description: formData.description || '',
      category: formData.category,
      subCategory: formData.subCategory || '',
      size: formData.file.size,
      type: formData.file.type,
      uploadedAt: new Date().toISOString(),
    };

    setTimeout(() => {
      onFilesAdded([newFile]);
      setIsUploading(false);
      setShowForm(false);
      setFormData({
        file: null,
        name: '',
        description: '',
        category: '',
        subCategory: '',
      });
    }, 500);
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      file: null,
      name: '',
      description: '',
      category: '',
      subCategory: '',
    });
    setUploadError(null);
  };

  const getFileIcon = (fileType: string, fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return <PictureAsPdf sx={{ fontSize: 16, color: '#DC2626' }} />;
    if (['doc', 'docx'].includes(ext)) return <Description sx={{ fontSize: 16, color: '#2563EB' }} />;
    if (['ppt', 'pptx'].includes(ext)) return <Description sx={{ fontSize: 16, color: '#D97706' }} />;
    if (['xls', 'xlsx'].includes(ext)) return <Description sx={{ fontSize: 16, color: '#16A34A' }} />;
    if (['jpg', 'jpeg', 'png', 'svg', 'gif'].includes(ext)) return <Image sx={{ fontSize: 16, color: '#7C3AED' }} />;
    return <InsertDriveFile sx={{ fontSize: 16, color: '#6B7280' }} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const availableSubCategories = formData.category ? subCategories[formData.category] || [] : [];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Existing Files */}
      {existingFiles.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {existingFiles.map((file) => (
            <Chip
              key={file.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 200 }}>
                  {getFileIcon(file.type, file.name)}
                  <Typography
                    sx={{
                      fontSize: 11,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 80,
                    }}
                  >
                    {file.name}
                  </Typography>
                  <Chip
                    label={file.category}
                    size="small"
                    sx={{ height: 14, fontSize: 8, bgcolor: 'rgba(124,58,237,0.1)' }}
                  />
                  <Typography sx={{ fontSize: 9, color: '#999' }}>
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
              }
              size="small"
              onDelete={onRemove ? () => onRemove(file.id) : undefined}
              deleteIcon={<Close sx={{ fontSize: 12 }} />}
              sx={{
                maxWidth: 240,
                '& .MuiChip-label': { px: 0.5 },
                borderColor: '#E5E7EB',
                bgcolor: '#FAFAFA',
              }}
            />
          ))}
        </Box>
      )}

      {/* Upload Button */}
      {!showForm && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<Upload sx={{ fontSize: 16 }} />}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            textTransform: 'none',
            fontSize: 12,
            borderRadius: '8px',
            py: 0.5,
            borderColor: '#7C3AED',
            color: '#7C3AED',
            '&:hover': { bgcolor: 'rgba(124,58,237,0.06)' },
          }}
        >
          Add Source File
        </Button>
      )}

      {/* Upload Form */}
      <Collapse in={showForm}>
        <Paper
          sx={{
            mt: 1,
            p: 2,
            border: '1px solid #E5E7EB',
            borderRadius: '10px',
            bgcolor: '#FAFAFA',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              File Details
            </Typography>
            <IconButton size="small" onClick={handleCancel}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          <Grid container spacing={1.5}>
            {/* File Preview */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  border: '1px solid #E5E7EB',
                }}
              >
                {formData.file && getFileIcon(formData.file.type, formData.file.name)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {formData.file?.name || 'No file selected'}
                  </Typography>
                  {formData.file && (
                    <Typography variant="caption" color="textSecondary">
                      {formatFileSize(formData.file.size)}
                    </Typography>
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: 'none', fontSize: 11 }}
                >
                  Change
                </Button>
              </Box>
            </Grid>

            {/* File Name */}
            <Grid item xs={12}>
              <TextField
                label="File Label *"
                placeholder="Enter a name for this file"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                size="small"
                sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                placeholder="What is this file about?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
                size="small"
                sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category *</InputLabel>
                <Select
                  value={formData.category}
                  label="Category *"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, subCategory: '' })}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sub-Category */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub-Category</InputLabel>
                <Select
                  value={formData.subCategory}
                  label="Sub-Category"
                  onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                  disabled={!formData.category}
                >
                  {availableSubCategories.map((sub) => (
                    <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {uploadError && (
            <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontSize: 12 }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleAddFile}
              disabled={!formData.file || !formData.category || isUploading}
              startIcon={isUploading ? <CircularProgress size={14} /> : <CheckCircle sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', fontSize: 12, borderRadius: '6px' }}
            >
              {isUploading ? 'Uploading...' : 'Add File'}
            </Button>
          </Box>
        </Paper>
      </Collapse>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.jpg,.jpeg,.png,.svg"
      />
    </Box>
  );
};

export default SourceFileUpload;